import { apiClient } from './client'
import { createEditionApi } from '@edition/api'
import type {
  EntitySubscriptionState,
  OwnerType,
  StakeholderFeedItem,
  StakeholderNotification,
  StatusDecision,
} from '../types'

/** What a reporter was told about their feedback, and when. Nothing more: there is nothing to answer it with. */
export interface FeedbackUpdateReceipt {
  notificationId: string
  title: string
  message: string
  sentAt: string
}

/**
 * What can be followed: the typed owners, plus `UNIT` for a level the tenant
 * added. The server takes all of them; OwnerType elsewhere does not carry UNIT
 * yet because status roll-ups have no template for it.
 */
export type FollowableType = OwnerType | 'UNIT'

export const stakeholderEngagementApi = {
  ...createEditionApi(apiClient).stakeholder,
  state: (entityType: FollowableType, entityId: string) =>
    apiClient.get<EntitySubscriptionState>('/api/v1/entity-subscriptions/state', {
      params: { entityType, entityId },
    }),

  follow: (entityType: FollowableType, entityId: string, notificationEnabled = true) =>
    apiClient.post<EntitySubscriptionState>('/api/v1/entity-subscriptions', {
      entityType,
      entityId,
      notificationEnabled,
    }),

  unfollow: (entityType: FollowableType, entityId: string) =>
    apiClient.delete<EntitySubscriptionState>('/api/v1/entity-subscriptions', {
      params: { entityType, entityId },
    }),

  feed: () =>
    apiClient.get<StakeholderFeedItem[]>('/api/v1/newsfeed'),

  notifications: () =>
    apiClient.get<StakeholderNotification[]>('/api/v1/notifications'),

  unreadCount: () =>
    apiClient.get<{ count: number }>('/api/v1/notifications/unread-count'),

  markAllRead: () =>
    apiClient.post<void>('/api/v1/notifications/read-all'),

  // Decision records (audit F-21)
  recordDecision: (statusId: string, body: { question: string; decision: string }) =>
    apiClient.post<StatusDecision>(`/api/v1/stakeholder-statuses/${statusId}/decisions`, body),

  decisionsForStatus: (statusId: string) =>
    apiClient.get<StatusDecision[]>(`/api/v1/stakeholder-statuses/${statusId}/decisions`),

  decisionsForOwner: (ownerType: OwnerType, ownerId: string) =>
    apiClient.get<StatusDecision[]>('/api/v1/stakeholder-status-decisions', {
      params: { ownerType, ownerId },
    }),
}
